import type Swiper from 'swiper';

export type EmulatedInfiniteOptions = {
  navigation: boolean;
  autoplayDelayMs: number | false;
  disableAutoplayOnInteraction?: boolean;
  pauseOnMouseEnter?: boolean;
};

export type EmulatedInfiniteControl = {
  setPaused: (paused: boolean) => void;
};

function getNavElements(swiper: Swiper): { next: Element | null; prev: Element | null } {
  const nav = swiper.navigation;
  if (!nav) return { next: null, prev: null };
  const nextEl = nav.nextEl;
  const prevEl = nav.prevEl;
  const next = typeof nextEl === 'string' ? document.querySelector(nextEl) : nextEl;
  const prev = typeof prevEl === 'string' ? document.querySelector(prevEl) : prevEl;
  return { next, prev };
}

function stripNavDisabled(swiper: Swiper): void {
  const nav = swiper.navigation;
  if (!nav) return;
  const dc =
    (swiper.params.navigation as { disabledClass?: string } | undefined)?.disabledClass ||
    'swiper-button-disabled';
  const { next, prev } = getNavElements(swiper);
  [next, prev].forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.classList.remove(dc);
    // Navigation and A11y disable the end buttons; the wrap needs them clickable and focusable.
    if (el instanceof HTMLButtonElement) el.disabled = false;
    el.removeAttribute('aria-disabled');
    el.setAttribute('tabindex', '0');
  });
}

function inferSwipeDirectionFromTouch(swiper: Swiper): 'next' | 'prev' | '' {
  const s = swiper as unknown as {
    touches?: { diff?: number };
    touchEventsData?: { startX?: number; currentX?: number };
    rtlTranslate?: boolean;
  };
  const rtl = Boolean(s.rtlTranslate);

  if (typeof s.touches?.diff === 'number') {
    const d = s.touches.diff;
    const threshold = 8;
    if (!rtl) {
      if (d < -threshold) return 'next';
      if (d > threshold) return 'prev';
    } else {
      if (d > threshold) return 'next';
      if (d < -threshold) return 'prev';
    }
  }

  const ted = s.touchEventsData;
  if (ted && typeof ted.startX === 'number' && typeof ted.currentX === 'number') {
    const delta = ted.currentX - ted.startX;
    const threshold = 25;
    if (!rtl) {
      if (delta < -threshold) return 'next';
      if (delta > threshold) return 'prev';
    } else {
      if (delta > threshold) return 'next';
      if (delta < -threshold) return 'prev';
    }
  }

  return '';
}

function wrapIfSwipedPastEdge(swiper: Swiper): void {
  if ((swiper.params as { direction?: string }).direction === 'vertical') return;

  let dir = (swiper as unknown as { swipeDirection?: 'next' | 'prev' | '' }).swipeDirection ?? '';
  if (!dir) dir = inferSwipeDirectionFromTouch(swiper);

  if (dir === 'next' && swiper.isEnd) {
    swiper.slideTo(0);
    return;
  }
  if (dir === 'prev' && swiper.isBeginning) {
    swiper.slideTo(Math.max(0, swiper.slides.length - 1));
  }
}

/**
 * Infinite prev/next without Swiper loop (avoids duplicate slides).
 * - Navigation arrows: wrap via capture-phase clicks when navigation is enabled.
 * - Autoplay: timer advances + touch wrap at ends (Swiper Autoplay stays off when used).
 * - When autoplay is off: no touch/drag wrap; linear swipe at the ends.
 */
export function attachEmulatedInfiniteWithoutLoop(
  swiper: Swiper,
  opts: EmulatedInfiniteOptions
): EmulatedInfiniteControl | undefined {
  const slideCount = swiper.slides?.length ?? 0;
  if (slideCount <= 1) return undefined;

  const hasAutoplay = opts.autoplayDelayMs !== false;
  if (!opts.navigation && !hasAutoplay) return undefined;

  let autoplayTimer: number | null = null;
  let autoplayPaused = false;
  let autoplayPausedByUser = false;

  const clearAutoplayTimer = () => {
    if (autoplayTimer != null) {
      window.clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  };

  const scheduleAutoplay = () => {
    if (opts.autoplayDelayMs === false || autoplayPaused || autoplayPausedByUser) return;
    clearAutoplayTimer();
    autoplayTimer = window.setTimeout(() => {
      autoplayTimer = null;
      if ((swiper as unknown as { destroyed?: boolean }).destroyed) return;
      if (swiper.isEnd) swiper.slideTo(0);
      else swiper.slideNext();
    }, opts.autoplayDelayMs);
  };

  const nextHandler = (e: Event) => {
    if (!swiper.isEnd) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    swiper.slideTo(0);
  };

  const prevHandler = (e: Event) => {
    if (!swiper.isBeginning) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    const last = Math.max(0, swiper.slides.length - 1);
    swiper.slideTo(last);
  };

  const { next: nextTarget, prev: prevTarget } = getNavElements(swiper);

  if (opts.navigation && nextTarget instanceof HTMLElement) {
    nextTarget.addEventListener('click', nextHandler, true);
  }
  if (opts.navigation && prevTarget instanceof HTMLElement) {
    prevTarget.addEventListener('click', prevHandler, true);
  }

  const onTransitionEnd = () => {
    if (opts.navigation) stripNavDisabled(swiper);
    if (hasAutoplay) scheduleAutoplay();
  };

  if (opts.navigation || hasAutoplay) {
    swiper.on('slideChangeTransitionEnd', onTransitionEnd);
    onTransitionEnd();
  }

  // Navigation disables the end button as the slide starts. Re-enabling it only at transition
  // end is too late: by then the browser has moved keyboard focus off the disabled button.
  const onToEdge = () => stripNavDisabled(swiper);
  if (opts.navigation) swiper.on('toEdge', onToEdge);

  let onMouseEnter: (() => void) | undefined;
  let onMouseLeave: (() => void) | undefined;
  if (hasAutoplay && opts.pauseOnMouseEnter) {
    const el = swiper.el;
    if (el instanceof HTMLElement) {
      onMouseEnter = () => {
        autoplayPaused = true;
        clearAutoplayTimer();
      };
      onMouseLeave = () => {
        autoplayPaused = false;
        scheduleAutoplay();
      };
      el.addEventListener('mouseenter', onMouseEnter);
      el.addEventListener('mouseleave', onMouseLeave);
    }
  }

  let onTouchEnd: (() => void) | undefined;
  if (hasAutoplay) {
    onTouchEnd = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => wrapIfSwipedPastEdge(swiper));
      });
      if (!opts.disableAutoplayOnInteraction) {
        window.setTimeout(() => scheduleAutoplay(), 50);
      }
    };
    swiper.on('touchEnd', onTouchEnd);
    swiper.on('touchStart', clearAutoplayTimer);
  }

  swiper.on('destroy', () => {
    clearAutoplayTimer();
    const el = swiper.el;
    if (el instanceof HTMLElement) {
      if (onMouseEnter) el.removeEventListener('mouseenter', onMouseEnter);
      if (onMouseLeave) el.removeEventListener('mouseleave', onMouseLeave);
    }
    if (onTouchEnd) {
      swiper.off('touchEnd', onTouchEnd);
      swiper.off('touchStart', clearAutoplayTimer);
    }
    if (opts.navigation || hasAutoplay) {
      swiper.off('slideChangeTransitionEnd', onTransitionEnd);
    }
    if (opts.navigation) swiper.off('toEdge', onToEdge);
    if (nextTarget instanceof HTMLElement) nextTarget.removeEventListener('click', nextHandler, true);
    if (prevTarget instanceof HTMLElement) prevTarget.removeEventListener('click', prevHandler, true);
  });

  return {
    setPaused: (paused: boolean) => {
      autoplayPausedByUser = paused;
      if (paused) clearAutoplayTimer();
      else scheduleAutoplay();
    },
  };
}
